import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, CircularProgress, Container, Pagination, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as DrainPipeHelper from '../../api/drainPipe/drainPipeHelper';
import { IGetDrainPipePreviewResponse } from '../../api/drainPipe/iApiResponse';
import { EStatusCode } from '../../api/enum';
import { IPaginatedResponse } from '../../api/httpClient';
import { route } from '../../route/Index';
import { EStatus } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { IDrainPipe } from '../../shared/iProject';
import { toastAlert } from '../../shared/util';

interface IDrainPipePanelProps {}

const DrainPipePanelContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const DrainPipePanel = (props: IDrainPipePanelProps) => {
    let pageSize = 4;

    let timer = React.useRef<NodeJS.Timeout | null>(null);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [drainPipePreview, setDrainPipePreview] = React.useState<IPaginatedResponse<IGetDrainPipePreviewResponse>>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);
    const [filterName, setFilterName] = React.useState<string | null>(null);

    const fetchDrainPipePreview = async (page: number, filter?: string) => {
        setIsLoading(true);
        let res = await DrainPipeHelper.getPaginatedDrainPipePreviewsAsync(page, pageSize, filter);
        setDrainPipePreview(res.data);
        setIsLoading(false);
    };

    React.useEffect(() => {
        fetchDrainPipePreview(pageNumber);
    }, [pageNumber]);

    const handleGoToDrainPipe = (id: number) => navigate(route.backofficeDrainPipe.replace(':param', id.toString()));

    const handleChangeStatus = async (toTest: boolean, id: number) => {
        let drainPipe = await DrainPipeHelper.getDrainPipeByIdAsync(id);
        let payload: IDrainPipe = {
            ...drainPipe.data!,
            status: toTest ? EStatus.Test : EStatus.Deleted,
        };
        let res = await DrainPipeHelper.updateDrainPipeAsync(payload);
        if (res.status == EStatusCode.Ok) {
            fetchDrainPipePreview(pageNumber);
            toastAlert('success', t('updatedDrainPipe'));
        }
    };

    React.useEffect(() => {
        if (filterName !== null) {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            timer.current = setTimeout(() => {
                fetchDrainPipePreview(1, filterName);
            }, 1000);
        }
    }, [filterName]);

    return (
        <DrainPipePanelContainer maxWidth='md'>
            <Stack height='100%' direction='column' spacing={4}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h1' gutterBottom>
                        {t('drainPipes')}
                    </Typography>
                    <Button variant='orange' endIcon={<AddCircleIcon />} onClick={() => navigate(route.backofficeDrainPipe.replace(':param', 'newDrainPipe'))}>
                        {t('addNewDrainPipe')}
                    </Button>
                </Stack>
                <TextField
                    id='input-search'
                    placeholder={t('searchName')}
                    InputProps={{
                        type: 'search',
                    }}
                    style={{ marginTop: '25px' }}
                    onChange={e => setFilterName(e.target.value)}
                />
                {isLoading && <CircularProgress />}
                {!isLoading && drainPipePreview?.value?.length! > 0 && (
                    <>
                        <Stack flexGrow={1}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size='small' aria-label='simple table'>
                                    <TableHead variant='greenLightWithoutBorder'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='center'>{t('lastUpdate')}</TableCell>
                                            <TableCell align='right'>{t('enabled')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {drainPipePreview!.value.map(x => (
                                            <TableRow key={x.name} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleGoToDrainPipe(x.id)} component='th' scope='row'>
                                                    {x.name}
                                                </TableCell>
                                                <TableCell onClick={() => handleGoToDrainPipe(x.id)} align='center'>
                                                    {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    <Switch
                                                        onChange={e => handleChangeStatus(e.target.checked, x.id)}
                                                        aria-label={`switch ${x.name}`}
                                                        checked={x.status == EStatus.Public || x.status == EStatus.Test}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        {drainPipePreview!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination
                                    showFirstButton
                                    showLastButton
                                    variant='outlined'
                                    shape='rounded'
                                    page={drainPipePreview?.pageNumber}
                                    count={drainPipePreview?.pageCount}
                                    onChange={(e, value) => setpageNumber(value)}
                                />
                            </Stack>
                        )}
                    </>
                )}
                {!isLoading && drainPipePreview?.value?.length! == 0 && <div>Empty</div>}
            </Stack>
        </DrainPipePanelContainer>
    );
};

export default isAdmin(DrainPipePanel);
